import { DenormalizedCustomizableGroup, DenormalizedCustomizableQuestion, EntityType, GroupType } from '@packages/types'
import classNames from 'classnames'
import React, { useRef, useEffect } from 'react'

import { useSelector, useDispatch } from 'customizer/hooks'
import { setMobileDescription } from 'customizer/questionPanel/actions'
import { mobileDescriptionSelector } from 'customizer/questionPanel/selectors'
import useThemeSettings from 'customizer/theme/useThemeSettings'
import BulkOrderQuantityAndErrorMessage from 'themes/common/components/bulkOrder/BulkOrderQuantityAndErrorMessage'

import useRenderedSteps from '../../hooks/useRenderedSteps'
import Summary from '../Summary'
import AnimatedStep from './AnimatedStep'
import './TitleNavigation.scss'
import DescriptionButton from './DescriptionButton'

type StepType = DenormalizedCustomizableGroup | DenormalizedCustomizableQuestion

export interface TitleNavigationProps {
  step?: StepType
  style?: React.CSSProperties
  steps: StepType[]
}

const TitleNavigation = ({ steps, step, style }: TitleNavigationProps) => {
  const ref = useRef(null)
  const summaryStyle = useThemeSettings('summary')
  const questionStyle = useThemeSettings('question')
  const questionPanelStyle = useThemeSettings('questionPanel')
  const mobileDescription = useSelector(mobileDescriptionSelector)

  const dispatch = useDispatch()
  const { renderedSteps, setStepAsRendered } = useRenderedSteps({ steps })

  useEffect(() => {
    const selectedStepId = step?.id

    if (selectedStepId && !renderedSteps[selectedStepId]) {
      setStepAsRendered(selectedStepId)
      dispatch(setMobileDescription(step))
    }
  }, [step?.id])

  if (!step) return null

  return (
    <div
      ref={ref}
      className="step__header"
      style={{
        background: questionPanelStyle.mobileHeaderBackgroundColor,
        borderColor: questionPanelStyle.mobileHeaderBorderColor,
        ...style,
      }}
      data-testid="title-navigation"
    >
      <AnimatedStep className="step__header-wrapper">
        <div
          data-testid="step-header-title"
          key={step.id}
          id={step.id}
          className={classNames('step__header-title step__header-title--active')}
          style={{
            color: questionStyle.questionFontColor,
            fontSize: questionStyle.questionFontSize,
            fontFamily: questionStyle.questionFontFamily?.fontFamily,
          }}
        >
          <div>
            <div className="step__header-title-description">
              <span className={classNames({ step__title: step.showDescription && mobileDescription?.id !== step.id })}>
                {step.name}
              </span>
              {step.showDescription && (
                <DescriptionButton
                  isVisible={mobileDescription?.id === step.id}
                  onClick={() => dispatch(setMobileDescription(step))}
                />
              )}
            </div>
            {step.type === GroupType.BulkOrder && (
              <BulkOrderQuantityAndErrorMessage
                bulkOrderGroup={step}
                shouldDisplayQuantityMessage
                shouldDisplayRequiredMessage
                shouldDisplayStockMessage
                shouldDisplayWordFilterMessage
              />
            )}
            {step.entityType === EntityType.Question && (
              <Summary
                showResolutionIndicator
                question={step}
                style={{
                  color: summaryStyle.color,
                  fontFamily: summaryStyle.fontFamily?.fontFamily,
                }}
              />
            )}
          </div>
        </div>
      </AnimatedStep>
    </div>
  )
}

export default TitleNavigation
